/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetAssistantSessionSummaryRequest,
  GetAssistantSessionSummarySecurity,
} from "../models/operations/getassistantsessionsummary.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AssistantSessionSummaryQueryData,
  buildAssistantSessionSummaryQuery,
  prefetchAssistantSessionSummary,
  queryKeyAssistantSessionSummary,
} from "./assistantSessionSummary.core.js";
export {
  type AssistantSessionSummaryQueryData,
  buildAssistantSessionSummaryQuery,
  prefetchAssistantSessionSummary,
  queryKeyAssistantSessionSummary,
};

export type AssistantSessionSummaryQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getAssistantSessionSummary chat
 *
 * @remarks
 * Get assistant session activity totals for a time range.
 */
export function useAssistantSessionSummary(
  request: GetAssistantSessionSummaryRequest,
  security?: GetAssistantSessionSummarySecurity | undefined,
  options?: QueryHookOptions<
    AssistantSessionSummaryQueryData,
    AssistantSessionSummaryQueryError
  >,
): UseQueryResult<
  AssistantSessionSummaryQueryData,
  AssistantSessionSummaryQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildAssistantSessionSummaryQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getAssistantSessionSummary chat
 *
 * @remarks
 * Get assistant session activity totals for a time range.
 */
export function useAssistantSessionSummarySuspense(
  request: GetAssistantSessionSummaryRequest,
  security?: GetAssistantSessionSummarySecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AssistantSessionSummaryQueryData,
    AssistantSessionSummaryQueryError
  >,
): UseSuspenseQueryResult<
  AssistantSessionSummaryQueryData,
  AssistantSessionSummaryQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAssistantSessionSummaryQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAssistantSessionSummaryData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      assistantId: string;
      from: Date;
      to: Date;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: AssistantSessionSummaryQueryData,
): AssistantSessionSummaryQueryData | undefined {
  const key = queryKeyAssistantSessionSummary(...queryKeyBase);

  return client.setQueryData<AssistantSessionSummaryQueryData>(key, data);
}

export function invalidateAssistantSessionSummary(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      assistantId: string;
      from: Date;
      to: Date;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "chat",
      "getAssistantSessionSummary",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAssistantSessionSummary(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "chat", "getAssistantSessionSummary"],
  });
}
